// src/components/programs/Table/ProgramsTableRow.jsx
import React from "react";
import {
    TableRow,
    TableCell,
    IconButton,
    Checkbox,
    Chip,
    Typography,
    Stack,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EventIcon from "@mui/icons-material/Event";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { formatCurrency, formatNumber } from "./../ProgramForm/helpers/quotationCalculations";

const ProgramsTableRow = ({
    program,
    index,
    isDark,
    page,
    perPage,
    sortBy,
    sortDir,
    totalItems,
    selectedIds,
    onRowCheckboxClick,
    onMenuOpen,
    isArchive = false,
    menuDisabled = false
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";

    const serial =
        sortDir === "asc"
            ? (page - 1) * perPage + index + 1
            : (page - 1) * perPage + (totalItems - index);

    const isOdd = index % 2 === 0;
    const isSelected = selectedIds.includes(program?.id);

    // Calculate duration display from the same fields used by API search.
    const durationDays = program?.durations != null && program?.durations !== ""
        ? Number(program.durations)
        : (program?.days?.length || 0);
    const durationNights = program?.nights != null && program?.nights !== ""
        ? Number(program.nights)
        : (durationDays > 0 ? durationDays - 1 : 0);
    const durationText = isArabic
        ? `${durationDays} أيام / ${durationNights} ليالي`
        : `${durationDays} days / ${durationNights} nights`;

    // Format discount
    const getDiscountDisplay = () => {
        const discount = program?.discount;

        if (discount == null || discount === 0) {
            return isArabic ? "لا يوجد" : "None";
        }

        if (
            program.discount_type === "percentage" ||
            program.discount_type === "percent"
        ) {
            return `${formatNumber(discount, 2)}%`;
        }

        // Fixed amount discount
        return formatCurrency(discount);
    };

    return (
        <TableRow
            hover
            sx={{
                backgroundColor: isOdd
                    ? isDark
                        ? "rgba(55,65,81,0.45)"
                        : "rgba(249,250,251,0.9)"
                    : "transparent",
            }}
        >
            <TableCell padding="checkbox">
                <Checkbox
                    color="primary"
                    checked={isSelected}
                    onClick={(e) => onRowCheckboxClick(e, program?.id)}
                    inputProps={{ "aria-labelledby": `program-${program?.id}` }}
                />
            </TableCell>

            <TableCell align="right">
                <IconButton
                    size="small"
                    disabled={menuDisabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        onMenuOpen(e, program);
                    }}
                    sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </TableCell>
            <TableCell>{serial}</TableCell>

            {/* Program Number */}
            <TableCell>
                <Chip
                    icon={<EventIcon />}
                    label={program?.program_number || "-"}
                    color="primary"
                    size="small"
                    variant="outlined"
                    sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                    }}
                />
            </TableCell>

            {/* Customer Name */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937", minWidth: 180 }}>
                <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
                    {program?.description?.[lng] || "-"}
                </Typography>
            </TableCell>
            {/* Title Arabic */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937", minWidth: 200 }}>
                <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
                    {program?.title?.ar || "-"}
                </Typography>
            </TableCell>

            {/* Title English */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937", minWidth: 200 }}>
                <Typography variant="body2" sx={{ whiteSpace: "normal" }}>
                    {program?.title?.en || "-"}
                </Typography>
            </TableCell>

            {/* Cities */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937", minWidth: 220 }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(() => {
                        const cities = program?.cities;
                        if (!cities || !Array.isArray(cities) || cities.length === 0) {
                            return (
                                <Typography variant="body2" sx={{ color: isDark ? "#9CA3AF" : "#6B7280" }}>
                                    -
                                </Typography>
                            );
                        }

                        return cities
                            .sort((a, b) => a.order - b.order)
                            .map((c, i) => {
                                const cityName = c.name?.[lng] || c.name?.en || c.name?.ar || '';
                                if (!cityName) return null;
                                return (
                                    <Chip
                                        key={c.id || i}
                                        label={cityName}
                                        size="small"
                                        sx={{
                                            fontSize: "0.7rem",
                                            fontWeight: 700,
                                            height: 22,
                                            backgroundColor: isDark ? "rgba(59, 130, 246, 0.12)" : "#EFF6FF",
                                            color: isDark ? "#60A5FA" : "#1D4ED8",
                                            border: `1px solid ${isDark ? "rgba(59, 130, 246, 0.2)" : "#DBEAFE"}`,
                                            "& .MuiChip-label": {
                                                px: 1
                                            }
                                        }}
                                    />
                                );
                            });
                    })()}
                </Stack>
            </TableCell>

            {/* Duration */}
            <TableCell>
                <Chip
                    label={durationText}
                    size="small"
                    variant="outlined"
                    sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                    }}
                />
            </TableCell>

            {/* Price */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}>
                <Typography variant="body2" fontWeight="bold" color="success.main">
                    {(() => {
                        const price = program?.display_price != null ? program.display_price : program?.price;
                        const currency = program?.display_currency || program?.currency;
                        const symbol = currency?.symbol || '฿';
                        return price != null ? `${symbol} ${formatCurrency(price)}` : "-";
                    })()}
                </Typography>
            </TableCell>

            {/* Discount */}
            <TableCell>
                <Chip
                    label={getDiscountDisplay()}
                    size="small"
                    color={program?.discount > 0 ? "warning" : "default"}
                    variant={program?.discount > 0 ? "filled" : "outlined"}
                    sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                    }}
                />
            </TableCell>

            {/* Confirmed */}
            <TableCell sx={{ width: 80, textAlign: "center" }}>
                <Chip
                    label={program?.confirmed ? (isArabic ? "نعم" : "Yes") : (isArabic ? "لا" : "No")}
                    size="small"
                    color={program?.confirmed ? "success" : "default"}
                    variant={program?.confirmed ? "filled" : "outlined"}
                    sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                    }}
                />
            </TableCell>

            {/* Confirm Number */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}>
                <Typography variant="body2">
                    {program?.confirmation_number || "-"}
                </Typography>
            </TableCell>

            {/* Closed */}
            <TableCell sx={{ width: 80, textAlign: "center" }}>
                <Chip
                    label={program?.closed ? (isArabic ? "نعم" : "Yes") : (isArabic ? "لا" : "No")}
                    size="small"
                    color={program?.closed ? "error" : "default"}
                    variant={program?.closed ? "filled" : "outlined"}
                    sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                    }}
                />
            </TableCell>

            {/* Close Number */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}>
                <Typography variant="body2">
                    {program?.close_number || "-"}
                </Typography>
            </TableCell>

            {/* Created By */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}>
                <Typography variant="body2">
                    {program?.created_by_user?.name || "-"}
                </Typography>
            </TableCell>

            {/* Updated At */}
            <TableCell sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}>
                <Typography variant="body2">
                    {program?.updated_at
                        ? format(new Date(program.updated_at), "dd/MM/yyyy HH:mm")
                        : "-"}
                </Typography>
            </TableCell>

            {/* Actions Menu */}
            <TableCell align="right">
                <IconButton
                    size="small"
                    disabled={menuDisabled}
                    onClick={(e) => {
                        e.stopPropagation();
                        onMenuOpen(e, program);
                    }}
                    sx={{ color: isDark ? "#E5E7EB" : "#1F2937" }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </TableCell>
        </TableRow>
    );
};

export default ProgramsTableRow;
